/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import {
  concat,
  concatAll,
  defaultIfEmpty,
  defer,
  distinct,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  fromEventPattern,
  map,
  merge,
  mergeMap,
  of,
  reduce,
  shareReplay,
  startWith,
  Subject,
  take,
  takeUntil,
  tap,
  toArray,
} from "rxjs";
import { assert, Id64 } from "@itwin/core-bentley";
import { PerModelCategoryVisibility } from "@itwin/core-frontend";
import { HierarchyNode, HierarchyNodeKey } from "@itwin/presentation-hierarchies";
import { enableCategoryDisplay, loadCategoriesFromViewport } from "../../common/CategoriesVisibilityUtils.js";
import { getTooltipOptions, mergeVisibilityStatuses, releaseMainThreadOnItemsCount, setDifference, setIntersection } from "../../common/internal/Utils.js";
import { toVoidPromise } from "../../common/Rxjs.js";
import { createVisibilityStatus } from "../../common/Tooltip.js";
import { createVisibilityHandlerResult } from "../../common/UseHierarchyVisibility.js";
import { getIdsFromChildrenTree } from "../Utils.js";
import { AlwaysAndNeverDrawnElementInfo } from "./AlwaysAndNeverDrawnElementInfo.js";
import { createFilteredTree, parseCategoryKey } from "./FilteredTree.js";
import { ModelsTreeNode } from "./ModelsTreeNode.js";
import { createVisibilityChangeEventListener } from "./VisibilityChangeEventListener.js";

import type { Observable, OperatorFunction, Subscription } from "rxjs";
import type { GuidString, Id64Arg, Id64Array, Id64Set, Id64String } from "@itwin/core-bentley";
import type { Viewport } from "@itwin/core-frontend";
import type { ClassGroupingNodeKey, GroupingHierarchyNode, HierarchyFilteringPath, InstancesNodeKey } from "@itwin/presentation-hierarchies";
import type { ECClassHierarchyInspector } from "@itwin/presentation-shared";
import type { Visibility } from "../../common/Tooltip.js";
import type { HierarchyVisibilityHandler, HierarchyVisibilityHandlerOverridableMethod, VisibilityStatus } from "../../common/UseHierarchyVisibility.js";
import type { GetElementsTreeProps } from "./AlwaysAndNeverDrawnElementInfo.js";
import type { FilteredTree } from "./FilteredTree.js";
import type { ModelsTreeIdsCache } from "./ModelsTreeIdsCache.js";
import type { IVisibilityChangeEventListener } from "./VisibilityChangeEventListener.js";

/** @beta */
interface GetCategoryVisibilityStatusProps {
  categoryIds: Id64Arg;
  modelId: Id64String;
}

/** @beta */
interface ChangeCategoryVisibilityStateProps extends GetCategoryVisibilityStatusProps {
  on: boolean;
}

/** @beta */
interface GetGeometricElementVisibilityStatusProps {
  elementId: Id64String;
  modelId: Id64String;
  categoryId: Id64String;
}

/** @beta */
interface ChangeGeometricElementsDisplayStateProps {
  elementIds: Id64Set;
  modelId: Id64String;
  categoryId: Id64String;
  on: boolean;
}

/** @beta */
interface ChangeModelVisibilityStateProps {
  ids: Id64Arg;
  on: boolean;
}

/** @beta */
interface GetFilteredNodeVisibilityProps {
  node: HierarchyNode & { key: ClassGroupingNodeKey | InstancesNodeKey };
}

/** @beta */
interface ChangeFilteredNodeVisibilityProps extends GetFilteredNodeVisibilityProps {
  on: boolean;
}

/**
 * Functionality of Models tree visibility handler that can be overridden.
 * Each callback is provided original implementation and reference to a `HierarchyVisibilityHandler`.
 * @beta
 */
export interface ModelsTreeVisibilityHandlerOverrides {
  getSubjectNodeVisibility?: HierarchyVisibilityHandlerOverridableMethod<(props: { ids: Id64Array }) => Promise<VisibilityStatus>>;
  getModelDisplayStatus?: HierarchyVisibilityHandlerOverridableMethod<(props: { ids: Id64Arg }) => Promise<VisibilityStatus>>;
  getCategoryDisplayStatus?: HierarchyVisibilityHandlerOverridableMethod<(props: GetCategoryVisibilityStatusProps) => Promise<VisibilityStatus>>;
  getElementGroupingNodeDisplayStatus?: HierarchyVisibilityHandlerOverridableMethod<(props: { node: GroupingHierarchyNode }) => Promise<VisibilityStatus>>;
  getElementDisplayStatus?: HierarchyVisibilityHandlerOverridableMethod<(props: GetGeometricElementVisibilityStatusProps) => Promise<VisibilityStatus>>;

  changeSubjectNodeState?: HierarchyVisibilityHandlerOverridableMethod<(props: { ids: Id64Array; on: boolean }) => Promise<void>>;
  changeModelState?: HierarchyVisibilityHandlerOverridableMethod<(props: ChangeModelVisibilityStateProps) => Promise<void>>;
  changeCategoryState?: HierarchyVisibilityHandlerOverridableMethod<(props: ChangeCategoryVisibilityStateProps) => Promise<void>>;
  changeElementGroupingNodeState?: HierarchyVisibilityHandlerOverridableMethod<(props: { node: GroupingHierarchyNode; on: boolean }) => Promise<void>>;
  changeElementsState?: HierarchyVisibilityHandlerOverridableMethod<(props: ChangeGeometricElementsDisplayStateProps) => Promise<void>>;
}

/**
 * Props for `createModelsTreeVisibilityHandler`.
 * @internal
 */
export interface ModelsTreeVisibilityHandlerProps {
  viewport: Viewport;
  idsCache: ModelsTreeIdsCache;
  imodelAccess: ECClassHierarchyInspector;
  overrides?: ModelsTreeVisibilityHandlerOverrides;
  filteredPaths?: HierarchyFilteringPath[];
  componentId?: GuidString;
}

/**
 * Creates an instance if `ModelsTreeVisibilityHandler`.
 * @internal
 */
export function createModelsTreeVisibilityHandler(props: ModelsTreeVisibilityHandlerProps): HierarchyVisibilityHandler & Disposable {
  return new ModelsTreeVisibilityHandlerImpl(props);
}

class ModelsTreeVisibilityHandlerImpl implements HierarchyVisibilityHandler {
  readonly #eventListener: IVisibilityChangeEventListener;
  readonly #alwaysAndNeverDrawnElements: AlwaysAndNeverDrawnElementInfo;
  readonly #idsCache: ModelsTreeIdsCache;
  #filteredTree: Promise<FilteredTree> | undefined;
  #elementChangeQueue = new Subject<Observable<void>>();
  #subscriptions: Subscription[] = [];
  #changeRequest = new Subject<{ key: HierarchyNodeKey; depth: number }>();
  readonly #props: ModelsTreeVisibilityHandlerProps;

  constructor(props: ModelsTreeVisibilityHandlerProps) {
    this.#props = props;
    this.#eventListener = createVisibilityChangeEventListener(this.#props.viewport);
    this.#alwaysAndNeverDrawnElements = new AlwaysAndNeverDrawnElementInfo(this.#props.viewport, this.#props.componentId);
    this.#idsCache = this.#props.idsCache;
    this.#filteredTree = this.#props.filteredPaths ? createFilteredTree(this.#props.imodelAccess, this.#props.filteredPaths) : undefined;
    this.#subscriptions.push(this.#elementChangeQueue.pipe(concatAll()).subscribe());
  }

  public get onVisibilityChange() {
    return this.#eventListener.onVisibilityChange;
  }

  public async getVisibilityStatus(node: HierarchyNode): Promise<VisibilityStatus> {
    return firstValueFrom(
      this.getVisibilityStatusObs(node).pipe(
        // unsubscribe from the observable if the change request for this node is received
        takeUntil(this.#changeRequest.pipe(filter(({ key, depth }) => depth === node.parentKeys.length && HierarchyNodeKey.equals(node.key, key)))),
        // unsubscribe if visibility changes
        takeUntil(
          fromEventPattern(
            (handler) => {
              this.#eventListener.onVisibilityChange.addListener(handler);
            },
            (handler) => {
              this.#eventListener.onVisibilityChange.removeListener(handler);
            },
          ),
        ),
        defaultIfEmpty(createVisibilityStatus("hidden")),
      ),
    );
  }

  public async changeVisibility(node: HierarchyNode, shouldDisplay: boolean): Promise<void> {
    // notify about new change request
    this.#changeRequest.next({ key: node.key, depth: node.parentKeys.length });

    const changeObservable = this.changeVisibilityObs(node, shouldDisplay).pipe(
      // unsubscribe from the observable if the change request for this node is received
      takeUntil(this.#changeRequest.pipe(filter(({ key, depth }) => depth === node.parentKeys.length && HierarchyNodeKey.equals(node.key, key)))),
      tap({
        subscribe: () => {
          this.#eventListener.suppressChangeEvents();
          this.#alwaysAndNeverDrawnElements.suppressChangeEvents();
        },
        finalize: () => {
          this.#eventListener.resumeChangeEvents();
          this.#alwaysAndNeverDrawnElements.resumeChangeEvents();
        },
      }),
    );

    return toVoidPromise(changeObservable);
  }

  public dispose() {
    this[Symbol.dispose]();
  }

  public [Symbol.dispose]() {
    this.#eventListener[Symbol.dispose]();
    this.#alwaysAndNeverDrawnElements[Symbol.dispose]();
    this.#subscriptions.forEach((x) => x.unsubscribe());
  }

  private getVisibilityStatusObs(node: HierarchyNode): Observable<VisibilityStatus> {
    if (HierarchyNode.isClassGroupingNode(node)) {
      if (node.extendedData?.hasDirectNonFilteredTargets && !node.filtering?.hasFilterTargetAncestor) {
        return this.getFilteredNodeVisibility({ node });
      }
      return this.getClassGroupingNodeDisplayStatus(node);
    }

    assert(HierarchyNode.isInstancesNode(node));

    // Only call getFilteredNodeVisibility when node is not a filter target, is not a child of filter target and has filtered children.
    // Otherwise, it can be handled normally.
    if (node.filtering?.filteredChildrenIdentifierPaths?.length && !node.filtering.isFilterTarget && !node.filtering.hasFilterTargetAncestor) {
      return this.getFilteredNodeVisibility({ node });
    }

    if (ModelsTreeNode.isSubjectNode(node)) {
      // note: subject nodes may be merged to represent multiple subject instances
      return this.getSubjectNodeVisibilityStatus({ subjectIds: node.key.instanceKeys.map((key) => key.id) });
    }

    if (ModelsTreeNode.isModelNode(node)) {
      return this.getModelVisibilityStatus({ modelIds: node.key.instanceKeys.map(({ id }) => id) });
    }

    const modelId = ModelsTreeNode.getModelId(node);
    assert(modelId !== undefined);

    if (ModelsTreeNode.isCategoryNode(node)) {
      return this.getCategoryDisplayStatus({
        categoryIds: node.key.instanceKeys.map(({ id }) => id),
        modelId,
      });
    }

    const categoryId = ModelsTreeNode.getCategoryId(node);
    const rootCategoryIds = getRootCategoryIds({ parentKeys: node.parentKeys, modelId });
    assert(rootCategoryIds !== undefined && categoryId !== undefined);
    return this.getElementDisplayStatus({
      elementIds: node.key.instanceKeys.map(({ id }) => id),
      parentKeys: node.parentKeys,
      rootCategoryIds,
      modelId,
      categoryId,
      totalChildrenCount: node.extendedData?.childrenCount ?? 0,
    });
  }

  private getFilteredNodeVisibility(props: GetFilteredNodeVisibilityProps) {
    assert(this.#filteredTree !== undefined);
    return from(this.#filteredTree).pipe(
      map((filteredTree) => filteredTree.getVisibilityChangeTargets(props.node)),
      mergeMap(({ subjects, models, categories, elements }) => {
        const observables = new Array<Observable<VisibilityStatus>>();
        if (subjects?.size) {
          observables.push(this.getSubjectNodeVisibilityStatus({ subjectIds: [...subjects], ignoreTooltip: true }));
        }

        if (models?.size) {
          observables.push(this.getModelVisibilityStatus({ modelIds: models, ignoreTooltip: true }));
        }

        if (categories?.size) {
          observables.push(
            from(categories).pipe(
              mergeMap((key) => {
                const { modelId, categoryId } = parseCategoryKey(key);
                return this.getCategoryDisplayStatus({ modelId, categoryIds: categoryId, ignoreTooltip: true });
              }),
            ),
          );
        }

        if (elements?.size) {
          const filterTargetElements = new Array<Id64String>();
          elements.forEach((elementsMap) =>
            elementsMap.forEach(({ isFilterTarget }, elementId) => {
              if (isFilterTarget) {
                filterTargetElements.push(elementId);
              }
            }),
          );
          let childrenCountMapObs: Observable<Map<Id64String, number>>;
          if (HierarchyNode.isClassGroupingNode(props.node)) {
            const groupingNodesFilterTargets: Map<Id64String, { childrenCount: number }> | undefined = props.node.extendedData?.filterTargets;
            const nestedFilterTargetElements = filterTargetElements.filter((filterTarget) => !groupingNodesFilterTargets?.has(filterTarget));
            // Only need to request children count for indirect children filter targets.
            // Direct children filter targets already have children count stored in grouping nodes extended data.
            childrenCountMapObs = this.#idsCache.getAllChildrenCount({ elementIds: nestedFilterTargetElements });
          } else {
            childrenCountMapObs = this.#idsCache.getAllChildrenCount({ elementIds: filterTargetElements });
          }
          observables.push(
            childrenCountMapObs.pipe(
              // Get children count for all filter target elements.
              map((elementCountMap) => {
                if (!HierarchyNode.isClassGroupingNode(props.node) || !props.node.extendedData?.filterTargets) {
                  return elementCountMap;
                }
                // Need to add children count (stored in grouping nodes' extended data) of direct children filter targets.
                const groupingNodesFilterTargets: Map<Id64String, { childrenCount: number }> = props.node.extendedData.filterTargets;
                groupingNodesFilterTargets.forEach((value, key) => elementCountMap.set(key, value.childrenCount));
                return elementCountMap;
              }),
              mergeMap((elementCountMap) =>
                from(elements).pipe(
                  releaseMainThreadOnItemsCount(50),
                  mergeMap(([categoryKey, elementsMap]) => {
                    const { modelId, categoryId } = parseCategoryKey(categoryKey);
                    let totalChildrenCount = 0;
                    elementsMap.forEach((_, elementId) => {
                      const childCount = elementCountMap.get(elementId);
                      if (childCount) {
                        totalChildrenCount += childCount;
                      }
                    });
                    const parentElementIdsPath = getParentElementsIdsPath({ parentKeys: props.node.parentKeys, modelId });
                    const rootCategoryIds = getRootCategoryIds({ parentKeys: props.node.parentKeys, modelId });
                    return this.getElementsDisplayStatus({
                      elementIds: [...elementsMap.keys()],
                      rootCategoryIds: rootCategoryIds ?? categoryId,
                      parentElementIdsPath,
                      modelId,
                      categoryId,
                      childrenCount: totalChildrenCount,
                    });
                  }),
                ),
              ),
            ),
          );
        }

        return merge(...observables);
      }),
      mergeVisibilityStatuses(),
    );
  }

  private getSubjectNodeVisibilityStatus({ subjectIds, ignoreTooltip }: { subjectIds: Id64Array; ignoreTooltip?: boolean }): Observable<VisibilityStatus> {
    const result = defer(() => {
      if (!this.#props.viewport.view.isSpatialView()) {
        return of(createVisibilityStatus("disabled", getTooltipOptions("modelsTree.subject.nonSpatialView", ignoreTooltip)));
      }

      return this.#idsCache.getSubjectModelIds(subjectIds).pipe(
        mergeMap((modelIds) => this.getModelVisibilityStatus({ modelIds, ignoreTooltip: true })),
        mergeVisibilityStatuses(
          {
            visible: "modelsTree.subject.allModelsVisible",
            hidden: "modelsTree.subject.allModelsHidden",
            partial: "modelsTree.subject.someModelsHidden",
          },
          ignoreTooltip,
        ),
      );
    });
    return createVisibilityHandlerResult(this, { ids: subjectIds }, result, this.#props.overrides?.getSubjectNodeVisibility);
  }

  private getModelVisibilityStatus({ modelIds, ignoreTooltip }: { modelIds: Id64Arg; ignoreTooltip?: boolean }): Observable<VisibilityStatus> {
    const result = defer(() => {
      const viewport = this.#props.viewport;
      if (!viewport.view.isSpatialView()) {
        return of(createVisibilityStatus("disabled", getTooltipOptions("modelsTree.model.nonSpatialView", ignoreTooltip)));
      }

      return from(Id64.iterable(modelIds)).pipe(
        distinct(),
        mergeMap((modelId) => {
          if (!viewport.view.viewsModel(modelId)) {
            return this.#idsCache.getModelCategories(modelId).pipe(
              mergeMap((categoryIds) => this.#idsCache.getCategoriesModeledElements(modelId, categoryIds)),
              this.getSubModeledElementsVisibilityStatus({
                ignoreTooltips: ignoreTooltip,
                haveSubModel: "yes",
                tooltips: { visible: undefined, hidden: "modelsTree.model.hiddenThroughModelSelector", partial: "modelsTree.model.someSubModelsVisible" },
                parentNodeVisibilityStatus: createVisibilityStatus("hidden"),
              }),
            );
          }
          return this.#idsCache.getModelCategories(modelId).pipe(
            mergeMap((categoryIds) => {
              return categoryIds.length === 0
                ? of(createVisibilityStatus("visible"))
                : this.getCategoryDisplayStatus({ modelId, categoryIds, ignoreTooltip: true });
            }),
            mergeVisibilityStatuses({
              visible: "modelsTree.model.allCategoriesVisible",
              partial: "modelsTree.model.someCategoriesHidden",
              hidden: "modelsTree.model.allCategoriesHidden",
            }),
          );
        }),
        mergeVisibilityStatuses(),
      );
    });
    return createVisibilityHandlerResult(this, { ids: modelIds }, result, this.#props.overrides?.getModelDisplayStatus);
  }

  private getDefaultCategoryVisibilityStatus({
    modelId,
    categoryIds,
    ignoreTooltip,
  }: {
    categoryIds: Id64Arg;
    modelId: Id64String;
    ignoreTooltip?: boolean;
  }): VisibilityStatus {
    const viewport = this.#props.viewport;

    if (!viewport.view.viewsModel(modelId) || Id64.sizeOf(categoryIds) === 0) {
      return createVisibilityStatus("hidden", getTooltipOptions("modelsTree.category.hiddenThroughModel", ignoreTooltip));
    }

    let visibility: "visible" | "hidden" | "unknown" = "unknown";
    for (const categoryId of Id64.iterable(categoryIds)) {
      const override = this.#props.viewport.perModelCategoryVisibility.getOverride(modelId, categoryId);
      if (override === PerModelCategoryVisibility.Override.Show) {
        if (visibility === "hidden") {
          return createVisibilityStatus("partial");
        }
        visibility = "visible";
        continue;
      }
      if (override === PerModelCategoryVisibility.Override.Hide) {
        if (visibility === "visible") {
          return createVisibilityStatus("partial");
        }
        visibility = "hidden";
        continue;
      }
      const isVisible = viewport.view.viewsCategory(categoryId);
      if (isVisible && visibility === "hidden") {
        return createVisibilityStatus("partial");
      }
      if (!isVisible && visibility === "visible") {
        return createVisibilityStatus("partial");
      }
      visibility = isVisible ? "visible" : "hidden";
    }
    assert(visibility !== "unknown");

    return createVisibilityStatus(visibility);
  }

  private getCategoryDisplayStatus({ ignoreTooltip, ...props }: GetCategoryVisibilityStatusProps & { ignoreTooltip?: boolean }): Observable<VisibilityStatus> {
    const result = defer(() => {
      if (!this.#props.viewport.view.viewsModel(props.modelId)) {
        return this.#idsCache.getCategoriesModeledElements(props.modelId, props.categoryIds).pipe(
          this.getSubModeledElementsVisibilityStatus({
            ignoreTooltips: ignoreTooltip,
            parentNodeVisibilityStatus: createVisibilityStatus("hidden"),
            tooltips: {
              visible: undefined,
              hidden: "modelsTree.category.hiddenThroughModel",
              partial: "modelsTree.category.someElementsOrSubModelsHidden",
            },
            haveSubModel: "yes",
          }),
        );
      }

      return this.getVisibilityFromAlwaysAndNeverDrawnElements({
        categoryProps: { categoryIds: props.categoryIds, modelId: props.modelId },
        tooltips: {
          allElementsInAlwaysDrawnList: "modelsTree.category.allElementsVisible",
          allElementsInNeverDrawnList: "modelsTree.category.allElementsHidden",
          elementsInBothAlwaysAndNeverDrawn: "modelsTree.category.someElementsAreHidden",
          noElementsInExclusiveAlwaysDrawnList: "modelsTree.category.allElementsHidden",
        },
        defaultStatus: (categoryId) =>
          categoryId
            ? this.getDefaultCategoryVisibilityStatus({ modelId: props.modelId, categoryIds: categoryId })
            : this.getDefaultCategoryVisibilityStatus({ modelId: props.modelId, categoryIds: props.categoryIds }),
        ignoreTooltip,
      }).pipe(
        mergeMap((visibilityStatusAlwaysAndNeverDraw) => {
          return this.#idsCache.getCategoriesModeledElements(props.modelId, props.categoryIds).pipe(
            this.getSubModeledElementsVisibilityStatus({
              tooltips: {
                visible: undefined,
                hidden: "modelsTree.category.allElementsAndSubModelsHidden",
                partial: "modelsTree.category.someElementsOrSubModelsHidden",
              },
              haveSubModel: "yes",
              parentNodeVisibilityStatus: visibilityStatusAlwaysAndNeverDraw,
              ignoreTooltips: ignoreTooltip,
            }),
          );
        }),
      );
    });
    return createVisibilityHandlerResult(this, props, result, this.#props.overrides?.getCategoryDisplayStatus);
  }

  private getClassGroupingNodeDisplayStatus(node: GroupingHierarchyNode): Observable<VisibilityStatus> {
    const { elementIds, modelId, categoryId, childrenCount } = this.getGroupingNodeInfo(node);
    const result = defer(() => {
      const rootCategoryIds = getRootCategoryIds({ parentKeys: node.parentKeys, modelId });
      assert(rootCategoryIds !== undefined);
      return this.getElementsDisplayStatus({
        elementIds,
        modelId,
        categoryId,
        rootCategoryIds,
        parentElementIdsPath: getParentElementsIdsPath({ parentKeys: node.parentKeys, modelId }),
        childrenCount: childrenCount ?? 0,
      });
    });
    return createVisibilityHandlerResult(this, { node }, result, this.#props.overrides?.getElementGroupingNodeDisplayStatus);
  }

  private getElementDisplayStatus({
    parentKeys,
    totalChildrenCount,
    elementIds,
    modelId,
    rootCategoryIds,
    categoryId,
  }: {
    elementIds: Id64Array;
    modelId: Id64String;
    categoryId: Id64String;
    rootCategoryIds: Id64Arg;
    parentKeys: HierarchyNodeKey[];
    totalChildrenCount: number;
  }): Observable<VisibilityStatus> {
    const result = this.getElementsDisplayStatus({
      rootCategoryIds,
      elementIds,
      modelId,
      categoryId,
      parentElementIdsPath: getParentElementsIdsPath({ parentKeys, modelId }),
      childrenCount: totalChildrenCount,
    });
    return createVisibilityHandlerResult(this, { elementId: elementIds[0], modelId, categoryId }, result, this.#props.overrides?.getElementDisplayStatus);
  }

  private getElementsDisplayStatus(props: {
    elementIds: Id64Array | Id64Set;
    modelId: Id64String;
    categoryId: Id64String;
    parentElementIdsPath: Array<Id64Arg>;
    rootCategoryIds: Id64Arg;
    childrenCount: number;
  }): Observable<VisibilityStatus> {
    return defer(() => {
      const { modelId, categoryId, elementIds, parentElementIdsPath, childrenCount, rootCategoryIds } = props;

      if (!this.#props.viewport.view.viewsModel(modelId)) {
        return of(elementIds).pipe(
          this.getSubModeledElementsVisibilityStatus({
            tooltips: {
              visible: undefined,
              hidden: undefined,
              partial: "modelsTree.groupingNode.someElementsOrSubModelsHidden",
            },
            parentNodeVisibilityStatus: createVisibilityStatus("hidden"),
            haveSubModel: "unknown",
          }),
        );
      }

      return this.getVisibilityFromAlwaysAndNeverDrawnElements({
        elements: elementIds,
        parentElementIdsPath,
        modelIds: modelId,
        rootCategoryIds,
        childrenCount,
        defaultStatus: () => {
          const status = this.getDefaultCategoryVisibilityStatus({ categoryIds: categoryId, modelId, ignoreTooltip: true });
          return createVisibilityStatus(status.state, getTooltipOptions(`modelsTree.groupingNode.${status.state}ThroughCategory`));
        },
        tooltips: {
          allElementsInAlwaysDrawnList: "modelsTree.groupingNode.allElementsVisible",
          allElementsInNeverDrawnList: "modelsTree.groupingNode.allElementsHidden",
          elementsInBothAlwaysAndNeverDrawn: "modelsTree.groupingNode.someElementsAreHidden",
          noElementsInExclusiveAlwaysDrawnList: "modelsTree.groupingNode.allElementsHidden",
        },
      }).pipe(
        mergeMap((visibilityStatusAlwaysAndNeverDraw) => {
          return of(elementIds).pipe(
            this.getSubModeledElementsVisibilityStatus({
              tooltips: {
                visible: undefined,
                hidden: "modelsTree.groupingNode.allElementsAndSubModelsHidden",
                partial: "modelsTree.groupingNode.someElementsOrSubModelsHidden",
              },
              parentNodeVisibilityStatus: visibilityStatusAlwaysAndNeverDraw,
              haveSubModel: "unknown",
            }),
          );
        }),
      );
    });
  }
  /** Changes visibility of the items represented by the tree node. */
  private changeVisibilityObs(node: HierarchyNode, on: boolean): Observable<void> {
    const changeObs = defer(() => {
      if (HierarchyNode.isClassGroupingNode(node)) {
        if (node.extendedData?.hasDirectNonFilteredTargets && !node.filtering?.hasFilterTargetAncestor) {
          return this.changeFilteredNodeVisibility({ node, on });
        }
        return this.changeElementGroupingNodeState(node, on);
      }

      if (!HierarchyNode.isInstancesNode(node)) {
        return EMPTY;
      }

      // Only call changeFilteredNodeVisibility when node is not a filter target, is not a child of filter target and has filtered children.
      // Otherwise, it can be handled normally.
      if (node.filtering?.filteredChildrenIdentifierPaths?.length && !node.filtering.isFilterTarget && !node.filtering.hasFilterTargetAncestor) {
        return this.changeFilteredNodeVisibility({ node, on });
      }

      if (ModelsTreeNode.isSubjectNode(node)) {
        return this.changeSubjectNodeState(
          node.key.instanceKeys.map((key) => key.id),
          on,
        );
      }

      if (ModelsTreeNode.isModelNode(node)) {
        return this.changeModelState({ ids: node.key.instanceKeys.map(({ id }) => id), on });
      }

      const modelId = ModelsTreeNode.getModelId(node);
      if (!modelId) {
        return EMPTY;
      }

      if (ModelsTreeNode.isCategoryNode(node)) {
        return this.changeCategoryState({
          categoryIds: node.key.instanceKeys.map(({ id }) => id),
          modelId,
          on,
        });
      }

      const categoryId = ModelsTreeNode.getCategoryId(node);
      if (!categoryId) {
        return EMPTY;
      }
      const elementIds = new Set(node.key.instanceKeys.map(({ id }) => id));
      return this.#idsCache.getChildrenTree({ elementIds }).pipe(
        map((childrenTree): Id64Set => {
          // Children tree contains provided elementIds, they are at the root of this tree.
          // We want to skip them and only get ids of children.
          return getIdsFromChildrenTree({ tree: childrenTree, predicate: ({ depth }) => depth > 0 });
        }),
        mergeMap((children) =>
          this.changeElementsState({
            elementIds,
            modelId,
            children: children.size > 0 ? children : undefined,
            categoryId,
            on,
          }),
        ),
      );
    });
    if (this.#props.viewport.isAlwaysDrawnExclusive) {
      return concat(this.removeAlwaysDrawnExclusive(), changeObs);
    }
    return changeObs;
  }

  private changeFilteredNodeVisibility({ on, ...props }: ChangeFilteredNodeVisibilityProps) {
    assert(this.#filteredTree !== undefined);
    return from(this.#filteredTree).pipe(
      map((filteredTree) => filteredTree.getVisibilityChangeTargets(props.node)),
      mergeMap(({ subjects, categories, elements, models }) => {
        const observables = new Array<Observable<void>>();
        if (subjects?.size) {
          observables.push(this.changeSubjectNodeState([...subjects], on));
        }

        if (models?.size) {
          observables.push(this.changeModelState({ ids: models, on }));
        }

        if (categories?.size) {
          observables.push(
            from(categories).pipe(
              mergeMap((key) => {
                const { modelId, categoryId } = parseCategoryKey(key);
                return this.changeCategoryState({ modelId, categoryIds: categoryId, on });
              }),
            ),
          );
        }

        if (elements?.size) {
          const filterTargetElements = new Array<Id64String>();
          const elementsSet = new Set<Id64String>();
          elements.forEach((elementsMap) =>
            elementsMap.forEach(({ isFilterTarget }, elementId) => {
              elementsSet.add(elementId);
              if (isFilterTarget) {
                filterTargetElements.push(elementId);
              }
            }),
          );
          observables.push(
            this.#idsCache.getChildrenTree({ elementIds: filterTargetElements }).pipe(
              map((childrenTree) => getIdsFromChildrenTree({ tree: childrenTree })),
              map((childrenIds) => setDifference(childrenIds, elementsSet)),
              mergeMap((childElementIds) =>
                from(elements).pipe(
                  mergeMap(([categoryKey, elementsMap]) => {
                    const { modelId, categoryId } = parseCategoryKey(categoryKey);
                    return this.changeElementsState({
                      modelId,
                      categoryId,
                      elementIds: new Set([...elementsMap.keys()]),
                      on,
                      children: childElementIds.size > 0 ? childElementIds : undefined,
                    });
                  }),
                ),
              ),
            ),
          );
        }

        return merge(...observables);
      }),
    );
  }

  private removeAlwaysDrawnExclusive(): Observable<void> {
    return this.#idsCache.getAllCategories().pipe(
      map((categoryIds) => {
        this.#props.viewport.changeCategoryDisplay(categoryIds, false, false, true);
        this.#props.viewport.clearNeverDrawn();
        this.#props.viewport.perModelCategoryVisibility.clearOverrides();
        this.#props.viewport.setAlwaysDrawn(this.#props.viewport.alwaysDrawn ?? new Set());
      }),
    );
  }

  private changeSubjectNodeState(ids: Id64Array, on: boolean): Observable<void> {
    const result = defer(() => {
      if (!this.#props.viewport.view.isSpatialView()) {
        return EMPTY;
      }

      return this.#idsCache.getSubjectModelIds(ids).pipe(mergeMap((modelIds) => this.changeModelState({ ids: modelIds, on })));
    });
    return createVisibilityHandlerResult(this, { ids, on }, result, this.#props.overrides?.changeSubjectNodeState);
  }

  private changeModelState(props: ChangeModelVisibilityStateProps): Observable<void> {
    const { ids, on } = props;

    if (Id64.sizeOf(ids) === 0) {
      return EMPTY;
    }

    const result = defer(() => {
      const viewport = this.#props.viewport;
      if (!viewport.view.isSpatialView()) {
        return EMPTY;
      }

      const idsObs = from(Id64.iterable(ids));
      if (!on) {
        viewport.changeModelDisplay(ids, false);
        return idsObs.pipe(
          mergeMap((modelId) => forkJoin({ categoryIds: this.#idsCache.getModelCategories(modelId), modelId: of(modelId) })),
          mergeMap(({ modelId, categoryIds }) => this.#idsCache.getCategoriesModeledElements(modelId, categoryIds)),
          mergeMap((modeledElementIds) => this.changeModelState({ ids: modeledElementIds, on })),
        );
      }

      return concat(
        defer(() => {
          viewport.perModelCategoryVisibility.clearOverrides(ids);
          return from(viewport.addViewedModels(ids));
        }),
        idsObs.pipe(
          mergeMap((modelId) => {
            return this.#idsCache.getModelCategories(modelId).pipe(mergeMap((categoryIds) => this.changeCategoryState({ categoryIds, modelId, on: true })));
          }),
        ),
      );
    });
    return createVisibilityHandlerResult(this, props, result, this.#props.overrides?.changeModelState);
  }

  private showModelWithoutAnyCategoriesOrElements(modelId: Id64String): Observable<void> {
    const viewport = this.#props.viewport;
    return forkJoin({
      categories: this.#idsCache.getModelCategories(modelId),
      alwaysDrawnElements: this.getAlwaysOrNeverDrawnElements({ modelIds: modelId, setType: "always" }),
    }).pipe(
      mergeMap(({ categories, alwaysDrawnElements }) => {
        const alwaysDrawn = this.#props.viewport.alwaysDrawn;
        if (alwaysDrawn && alwaysDrawnElements) {
          viewport.setAlwaysDrawn(setDifference(alwaysDrawn, alwaysDrawnElements));
        }

        return concat(
          from(Id64.iterable(categories)).pipe(
            releaseMainThreadOnItemsCount(300),
            map((categoryId) => this.changeCategoryStateInViewportAccordingToModelVisibility(modelId, categoryId, false)),
          ),
          defer(async () => viewport.addViewedModels(modelId)),
        );
      }),
    );
  }

  private changeCategoryStateInViewportAccordingToModelVisibility(modelId: string, categoryId: string, on: boolean) {
    const viewport = this.#props.viewport;
    const isDisplayedInSelector = viewport.view.viewsCategory(categoryId);
    const override =
      on === isDisplayedInSelector
        ? PerModelCategoryVisibility.Override.None
        : on
          ? PerModelCategoryVisibility.Override.Show
          : PerModelCategoryVisibility.Override.Hide;
    viewport.perModelCategoryVisibility.setOverride(modelId, categoryId, override);
    if (override === PerModelCategoryVisibility.Override.None && on) {
      // we took off the override which means the category is displayed in selector, but
      // doesn't mean all its subcategories are displayed - this call ensures that
      viewport.changeCategoryDisplay(categoryId, true, true, true);
    }
  }

  private changeCategoryState(props: ChangeCategoryVisibilityStateProps): Observable<void> {
    const result = defer(() => {
      const viewport = this.#props.viewport;
      const { modelId, categoryIds, on } = props;
      return concat(
        props.on && !viewport.view.viewsModel(modelId) ? this.showModelWithoutAnyCategoriesOrElements(modelId) : EMPTY,
        from(Id64.iterable(categoryIds)).pipe(
          releaseMainThreadOnItemsCount(300),
          map((categoryId) => this.changeCategoryStateInViewportAccordingToModelVisibility(modelId, categoryId, on)),
        ),
        this.clearAlwaysAndNeverDrawnElements(props),
        this.#idsCache
          .getCategoriesModeledElements(modelId, categoryIds)
          .pipe(mergeMap((modeledElementIds) => this.changeModelState({ ids: modeledElementIds, on }))),
      );
    });
    return createVisibilityHandlerResult(this, props, result, this.#props.overrides?.changeCategoryState);
  }

  private doChangeElementsState(
    props: ChangeGeometricElementsDisplayStateProps & {
      children: Id64Arg | undefined;
    },
  ): Observable<void | undefined> {
    return defer(() => {
      const { modelId, categoryId, elementIds, on, children } = props;
      const viewport = this.#props.viewport;
      return concat(
        on && !viewport.view.viewsModel(modelId) ? this.showModelWithoutAnyCategoriesOrElements(modelId) : EMPTY,
        defer(() => {
          const categoryVisibility = this.getDefaultCategoryVisibilityStatus({ categoryIds: categoryId, modelId, ignoreTooltip: true });
          const isCategoryVisible = categoryVisibility.state === "visible";
          // Make sure to add all children to always/never drawn list.
          const isDisplayedByDefault =
            // When category is visible and elements need to be turned off, or when category is hidden and elements need to be turned on,
            // We can set isDisplayedByDefault to isCategoryVisible. This allows to not check if each element is in the elementIds list or not.
            isCategoryVisible === !on
              ? () => isCategoryVisible
              : (elementId: Id64String) => {
                  if (elementIds.has(elementId)) {
                    return isCategoryVisible;
                  }
                  return !on;
                };
          const elementsToChange = children ? [...elementIds, ...(typeof children === "string" ? [children] : children)] : elementIds;
          return this.queueElementsVisibilityChange(elementsToChange, on, isDisplayedByDefault);
        }),
        from(elementIds).pipe(
          mergeMap((elementId) =>
            forkJoin({
              elementId: of(elementId),
              isSubModel: this.#idsCache.hasSubModel(elementId),
            }),
          ),
          filter(({ isSubModel }) => isSubModel),
          map(({ elementId }) => elementId),
          toArray(),
          mergeMap((subModelIds) => this.changeModelState({ ids: subModelIds, on })),
        ),
      );
    });
  }

  /**
   * Updates visibility of all grouping node's elements.
   * @see `changeElementState`
   */
  private changeElementGroupingNodeState(node: GroupingHierarchyNode, on: boolean): Observable<void> {
    const { modelId, categoryId, elementIds } = this.getGroupingNodeInfo(node);
    const result = this.#idsCache.getChildrenTree({ elementIds }).pipe(
      map((childrenTree) => getIdsFromChildrenTree({ tree: childrenTree, predicate: ({ depth }) => depth > 0 })),
      mergeMap((children) =>
        this.doChangeElementsState({
          modelId,
          categoryId,
          elementIds,
          children: children.size > 0 ? children : undefined,
          on,
        }),
      ),
    );
    return createVisibilityHandlerResult(this, { node, on }, result, this.#props.overrides?.changeElementGroupingNodeState);
  }

  /**
   * Updates visibility of an element and all its child elements by adding them to the always/never drawn list.
   * @note If element is to be enabled and model is hidden, it will be enabled.
   */
  private changeElementsState(props: ChangeGeometricElementsDisplayStateProps & { children: Id64Arg | undefined }): Observable<void> {
    const result = this.doChangeElementsState(props);
    return createVisibilityHandlerResult(this, props, result, this.#props.overrides?.changeElementsState);
  }

  private queueElementsVisibilityChange(elementIds: Id64Arg, on: boolean, visibleByDefault: (elementId: Id64String) => boolean) {
    const finishedSubject = new Subject<boolean>();
    // observable to track if visibility change is finished/cancelled
    const changeFinished = finishedSubject.pipe(
      startWith(false),
      shareReplay(1),
      filter((finished) => finished),
    );

    const changeObservable = from(Id64.iterable(elementIds)).pipe(
      // check if visibility change is not finished (cancelled) due to change overall change request being cancelled
      takeUntil(changeFinished),
      this.changeElementStateNoChildrenOperator({ on, isDisplayedByDefault: visibleByDefault }),
      tap({
        next: () => {
          // notify that visibility change is finished
          finishedSubject.next(true);
        },
      }),
    );

    // queue visibility change. `changeObservable` will be subscribed to when other queue changes are finished
    this.#elementChangeQueue.next(changeObservable);

    // return observable that will emit when visibility change is finished
    return changeFinished.pipe(
      take(1),
      tap({
        unsubscribe: () => {
          // if this observable is unsubscribed before visibility change is finished, we have to notify that it queued change request is cancelled
          finishedSubject.next(true);
        },
      }),
      map(() => undefined),
    );
  }

  private changeElementStateNoChildrenOperator(props: {
    on: boolean;
    isDisplayedByDefault: (elementId: Id64String) => boolean;
  }): OperatorFunction<string, void> {
    return (elementIds: Observable<Id64String>) => {
      const { on, isDisplayedByDefault } = props;
      const isAlwaysDrawnExclusive = this.#props.viewport.isAlwaysDrawnExclusive;
      return elementIds.pipe(
        releaseMainThreadOnItemsCount(500),
        reduce<string, { changedNeverDrawn: boolean; changedAlwaysDrawn: boolean; neverDrawn: Id64Set | undefined; alwaysDrawn: Id64Set | undefined }>(
          (acc, elementId) => {
            if (acc.alwaysDrawn === undefined || acc.neverDrawn === undefined) {
              acc.alwaysDrawn = new Set(this.#props.viewport.alwaysDrawn || []);
              acc.neverDrawn = new Set(this.#props.viewport.neverDrawn || []);
            }
            if (on) {
              const wasRemoved = acc.neverDrawn.delete(elementId);
              acc.changedNeverDrawn ||= wasRemoved;
              // If exclusive mode is enabled, we must add the element to the always drawn list.
              if ((!isDisplayedByDefault(elementId) || isAlwaysDrawnExclusive) && !acc.alwaysDrawn.has(elementId)) {
                acc.alwaysDrawn.add(elementId);
                acc.changedAlwaysDrawn = true;
              }
            } else {
              const wasRemoved = acc.alwaysDrawn.delete(elementId);
              acc.changedAlwaysDrawn ||= wasRemoved;
              // If exclusive mode is not enabled, we have to add the element to the never drawn list.
              if (isDisplayedByDefault(elementId) && !isAlwaysDrawnExclusive && !acc.neverDrawn.has(elementId)) {
                acc.neverDrawn.add(elementId);
                acc.changedNeverDrawn = true;
              }
            }
            return acc;
          },
          {
            changedNeverDrawn: false,
            changedAlwaysDrawn: false,
            neverDrawn: undefined,
            alwaysDrawn: undefined,
          },
        ),
        map((state) => {
          state.changedNeverDrawn && state.neverDrawn && this.#props.viewport.setNeverDrawn(state.neverDrawn);
          state.changedAlwaysDrawn && state.alwaysDrawn && this.#props.viewport.setAlwaysDrawn(state.alwaysDrawn, this.#props.viewport.isAlwaysDrawnExclusive);
        }),
      );
    };
  }

  private getVisibilityFromAlwaysAndNeverDrawnElementsImpl(
    props: {
      alwaysDrawn: Id64Arg | undefined;
      neverDrawn: Id64Arg | undefined;
      totalCount: number;
    } & GetVisibilityFromAlwaysAndNeverDrawnElementsProps & { ignoreTooltip?: boolean },
  ): VisibilityStatus {
    const { alwaysDrawn, neverDrawn, totalCount, ignoreTooltip } = props;
    if (totalCount === 0) {
      return props.defaultStatus();
    }
    const neverDrawnSize = neverDrawn ? Id64.sizeOf(neverDrawn) : undefined;
    const alwaysDrawnSize = alwaysDrawn ? Id64.sizeOf(alwaysDrawn) : undefined;
    if (neverDrawnSize === totalCount) {
      return createVisibilityStatus("hidden", getTooltipOptions(props.tooltips.allElementsInNeverDrawnList, ignoreTooltip));
    }

    if (alwaysDrawnSize === totalCount) {
      return createVisibilityStatus("visible", getTooltipOptions(props.tooltips.allElementsInAlwaysDrawnList, ignoreTooltip));
    }

    const viewport = this.#props.viewport;
    if (viewport.isAlwaysDrawnExclusive && viewport.alwaysDrawn?.size) {
      return alwaysDrawnSize
        ? createVisibilityStatus("partial", getTooltipOptions(props.tooltips.elementsInBothAlwaysAndNeverDrawn, ignoreTooltip))
        : createVisibilityStatus("hidden", getTooltipOptions(props.tooltips.noElementsInExclusiveAlwaysDrawnList, ignoreTooltip));
    }

    const status = props.defaultStatus();
    if ((status.state === "visible" && neverDrawnSize) || (status.state === "hidden" && alwaysDrawnSize)) {
      return createVisibilityStatus("partial", getTooltipOptions(undefined, ignoreTooltip));
    }
    return status;
  }

  private getVisibilityFromAlwaysAndNeverDrawnElements({
    ignoreTooltip,
    ...props
  }: GetVisibilityFromAlwaysAndNeverDrawnElementsProps &
    (
      | { elements: Id64Set | Id64Array; parentElementIdsPath: Array<Id64Arg>; childrenCount: number; modelIds: Id64Arg; rootCategoryIds: Id64Arg }
      | { categoryProps: { categoryIds: Id64Arg; modelId: Id64String } }
    ) & {
      ignoreTooltip?: boolean;
    }): Observable<VisibilityStatus> {
    const viewport = this.#props.viewport;
    if (viewport.isAlwaysDrawnExclusive) {
      if (!viewport?.alwaysDrawn?.size) {
        return of(createVisibilityStatus("hidden", getTooltipOptions(props.tooltips.noElementsInExclusiveAlwaysDrawnList, ignoreTooltip)));
      }
    } else if (!viewport?.neverDrawn?.size && !viewport?.alwaysDrawn?.size) {
      return of(props.defaultStatus());
    }

    if ("elements" in props) {
      const parentElementIdsPath = [...props.parentElementIdsPath, props.elements];
      return forkJoin({
        childAlwaysDrawn: this.getAlwaysOrNeverDrawnElements({
          modelIds: props.modelIds,
          categoryIds: props.rootCategoryIds,
          parentElementIdsPath,
          setType: "always",
        }),
        childNeverDrawn: this.getAlwaysOrNeverDrawnElements({
          modelIds: props.modelIds,
          categoryIds: props.rootCategoryIds,
          parentElementIdsPath,
          setType: "never",
        }),
      }).pipe(
        map(({ childAlwaysDrawn, childNeverDrawn }) => {
          const alwaysDrawn = new Set([...childAlwaysDrawn, ...(viewport.alwaysDrawn?.size ? setIntersection(props.elements, viewport.alwaysDrawn) : [])]);
          const neverDrawn = new Set([...childNeverDrawn, ...(viewport.neverDrawn?.size ? setIntersection(props.elements, viewport.neverDrawn) : [])]);
          return this.getVisibilityFromAlwaysAndNeverDrawnElementsImpl({
            ...props,
            alwaysDrawn: alwaysDrawn.size > 0 ? alwaysDrawn : undefined,
            neverDrawn: neverDrawn.size > 0 ? neverDrawn : undefined,
            totalCount: props.childrenCount + Id64.sizeOf(props.elements),
            ignoreTooltip,
          });
        }),
      );
    }
    const { modelId, categoryIds } = props.categoryProps;
    return from(Id64.iterable(categoryIds)).pipe(
      releaseMainThreadOnItemsCount(100),
      mergeMap((categoryId) => {
        return forkJoin({
          categoryId: of(categoryId),
          totalCount: this.#idsCache.getCategoryElementsCount(modelId, categoryId),
          alwaysDrawn: this.getAlwaysOrNeverDrawnElements({ modelIds: modelId, categoryIds: categoryId, setType: "always" }),
          neverDrawn: this.getAlwaysOrNeverDrawnElements({ modelIds: modelId, categoryIds: categoryId, setType: "never" }),
        });
      }),
      // There is a known bug:
      // Categories that don't have root elements will make visibility result incorrect
      // E.g.:
      // - CategoryA
      //  - ElementA (CategoryA is visible)
      //    - ChildElementB (CategoryB is hidden) ChildElementB is in always drawn list
      // Result will be "partial" because CategoryB will return hidden visibility, even though all elements are visible
      // TODO fix with: https://github.com/iTwin/viewer-components-react/issues/1100
      mergeMap((state) => {
        if (this.#props.viewport.isAlwaysDrawnExclusive && state.totalCount === 0) {
          return EMPTY;
        }
        return of(
          this.getVisibilityFromAlwaysAndNeverDrawnElementsImpl({
            ...props,
            ...state,
            defaultStatus: () => props.defaultStatus(state.categoryId),
            ignoreTooltip,
          }),
        );
      }),
      mergeVisibilityStatuses(),
    );
  }

  private getAlwaysOrNeverDrawnElements(props: GetElementsTreeProps): Observable<Id64Set> {
    if (!this.#filteredTree) {
      return this.#alwaysAndNeverDrawnElements
        .getElementsTree(props)
        .pipe(map((childrenTree) => getIdsFromChildrenTree({ tree: childrenTree, predicate: ({ treeEntry }) => treeEntry.isInAlwaysOrNeverDrawnSet })));
    }
    // When filtered tree is present, children tree retrieved from alwaysAndNeverDrawnElements may include elements that are not present in filtered tree.
    // Need to filter out such elements.
    return forkJoin({
      filteredTree: from(this.#filteredTree),
      childrenTree: this.#alwaysAndNeverDrawnElements.getElementsTree(props),
    }).pipe(
      map(({ filteredTree, childrenTree }) => {
        const parentIdsPath = [props.modelIds];
        if ("categoryIds" in props) {
          parentIdsPath.push(props.categoryIds);
          if ("parentElementIdsPath" in props) {
            props.parentElementIdsPath.forEach((parentElementIds) => parentIdsPath.push(parentElementIds));
          }
        }
        // Get all ids that exist in filtered tree.
        const elements = filteredTree.getElementsFromUnfilteredChildrenTree({ parentIdsPath, childrenTree });
        return elements
          ? // Need to filter elements that are in always/never drawn set.
            setIntersection(elements, getIdsFromChildrenTree({ tree: childrenTree, predicate: ({ treeEntry }) => treeEntry.isInAlwaysOrNeverDrawnSet }))
          : new Set();
      }),
    );
  }

  private clearAlwaysAndNeverDrawnElements(props: { categoryIds: Id64Arg; modelId: Id64String }) {
    return forkJoin({
      alwaysDrawn: this.getAlwaysOrNeverDrawnElements({ modelIds: props.modelId, categoryIds: props.categoryIds, setType: "always" }),
      neverDrawn: this.getAlwaysOrNeverDrawnElements({ modelIds: props.modelId, categoryIds: props.categoryIds, setType: "never" }),
    }).pipe(
      map(({ alwaysDrawn, neverDrawn }) => {
        const viewport = this.#props.viewport;
        if (viewport.alwaysDrawn?.size && alwaysDrawn.size) {
          viewport.setAlwaysDrawn(setDifference(viewport.alwaysDrawn, alwaysDrawn));
        }
        if (viewport.neverDrawn?.size && neverDrawn.size) {
          viewport.setNeverDrawn(setDifference(viewport.neverDrawn, neverDrawn));
        }
      }),
    );
  }

  private getGroupingNodeInfo(node: GroupingHierarchyNode) {
    const modelId = ModelsTreeNode.getModelId(node);
    const categoryId = ModelsTreeNode.getCategoryId(node);
    assert(!!modelId && !!categoryId);

    const elementIds = new Set(node.groupedInstanceKeys.map((key) => key.id));
    const childrenCount: number | undefined = node.extendedData?.childrenCount;
    const isFiltered: boolean = node.extendedData?.isFiltered;
    return { modelId, categoryId, elementIds, childrenCount, isFiltered };
  }

  private getSubModeledElementsVisibilityStatus({
    parentNodeVisibilityStatus,
    haveSubModel,
    tooltips,
    ignoreTooltips,
  }: {
    parentNodeVisibilityStatus: VisibilityStatus;
    haveSubModel: "yes" | "unknown";
    tooltips: { [key in Visibility]: string | undefined };
    ignoreTooltips?: boolean;
  }): OperatorFunction<Id64Arg, VisibilityStatus> {
    return (obs) => {
      return obs.pipe(
        // ensure we're only looking at elements that have a sub-model
        mergeMap((modeledElementIds) => {
          if (haveSubModel === "yes") {
            return of(modeledElementIds);
          }
          return from(Id64.iterable(modeledElementIds)).pipe(
            releaseMainThreadOnItemsCount(100),
            mergeMap((elementId) =>
              forkJoin({
                hasSubModel: this.#idsCache.hasSubModel(elementId),
                elementId: of(elementId),
              }),
            ),
            filter(({ hasSubModel }) => hasSubModel),
            map(({ elementId }) => elementId),
            toArray(),
          );
        }),
        // combine visibility status of sub-models with visibility status of parent node
        mergeMap((modeledElementIds) => {
          if (Id64.sizeOf(modeledElementIds) === 0) {
            return of(parentNodeVisibilityStatus);
          }
          return this.getModelVisibilityStatus({ modelIds: typeof modeledElementIds === "string" ? [modeledElementIds] : modeledElementIds }).pipe(
            startWith<VisibilityStatus>(parentNodeVisibilityStatus),
            mergeVisibilityStatuses(tooltips, ignoreTooltips),
          );
        }),
      );
    };
  }
}

interface GetVisibilityFromAlwaysAndNeverDrawnElementsProps {
  tooltips: {
    allElementsInNeverDrawnList: string;
    allElementsInAlwaysDrawnList: string;
    elementsInBothAlwaysAndNeverDrawn: string;
    noElementsInExclusiveAlwaysDrawnList: string;
  };
  /** Status when always/never lists are empty and exclusive mode is off */
  defaultStatus: (categoryId?: Id64String) => VisibilityStatus;
}

/**
 * Enables display of all given models. Also enables display of all categories and clears always and
 * never drawn lists in the viewport.
 *
 * @param componentId Optional unique id of the component that consumes this function.
 * It can be any string, as long as it is not shared across different components.
 * @public
 */
export async function showAllModels(models: string[], viewport: Viewport, componentId?: GuidString) {
  await viewport.addViewedModels(models);
  viewport.clearNeverDrawn();
  viewport.clearAlwaysDrawn();

  const categories = await loadCategoriesFromViewport(viewport, componentId);
  if (categories.length === 0) {
    return;
  }
  const ids = categories.map((category) => category.categoryId);
  await enableCategoryDisplay(viewport, ids, true);
}

/**
 * Disables display of all given models.
 * @public
 */
export async function hideAllModels(models: string[], viewport: Viewport) {
  viewport.changeModelDisplay(models, false);
}

/**
 * Inverts display of all given models.
 * @public
 */
export async function invertAllModels(models: string[], viewport: Viewport) {
  const notViewedModels: string[] = [];
  const viewedModels: string[] = [];
  models.forEach((modelId) => {
    if (viewport.viewsModel(modelId)) {
      viewedModels.push(modelId);
    } else {
      notViewedModels.push(modelId);
    }
  });
  await viewport.addViewedModels(notViewedModels);
  viewport.changeModelDisplay(viewedModels, false);
}

/**
 * Based on the value of `enable` argument, either enables or disables display of given models.
 * @public
 */
export async function toggleModels(models: string[], enable: boolean, viewport: Viewport) {
  if (!models) {
    return;
  }
  if (enable) {
    viewport.changeModelDisplay(models, false);
  } else {
    await viewport.addViewedModels(models);
  }
}

/**
 * Checks if all given models are displayed in given viewport.
 * @public
 */
export function areAllModelsVisible(models: string[], viewport: Viewport) {
  return models.length !== 0 ? models.every((id) => viewport.viewsModel(id)) : false;
}

function getParentElementsIdsPath(props: { parentKeys: HierarchyNodeKey[]; modelId: Id64String }): Array<Id64Arg> {
  const parentElementIdsPath = new Array<Id64Arg>();
  const modelIndex = props.parentKeys.findIndex(
    (parentKey) => HierarchyNodeKey.isInstances(parentKey) && parentKey.instanceKeys.some((instanceKey) => instanceKey.id === props.modelId),
  );
  if (modelIndex === -1) {
    return parentElementIdsPath;
  }
  // Hierarchy is model -> category -> root elements.
  // Root elements index is 2 more than models'.
  const firstParentIndex = modelIndex + 2;
  for (let i = firstParentIndex; i < props.parentKeys.length; ++i) {
    const parentKey = props.parentKeys[i];
    if (!HierarchyNodeKey.isInstances(parentKey)) {
      continue;
    }
    parentElementIdsPath.push(parentKey.instanceKeys.map(({ id }) => id));
  }
  return parentElementIdsPath;
}

function getRootCategoryIds(props: { parentKeys: HierarchyNodeKey[]; modelId: Id64String }): Id64Arg | undefined {
  const modelIndex = props.parentKeys.findIndex(
    (parentKey) => HierarchyNodeKey.isInstances(parentKey) && parentKey.instanceKeys.some((instanceKey) => instanceKey.id === props.modelId),
  );
  if (modelIndex === -1 || modelIndex + 1 === props.parentKeys.length) {
    return undefined;
  }
  const rootCategoryKeys = props.parentKeys[modelIndex + 1];
  if (!HierarchyNodeKey.isInstances(rootCategoryKeys)) {
    return undefined;
  }
  return rootCategoryKeys.instanceKeys.map(({ id }) => id);
}
